import { Button, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Select } from "@blueprintjs/select";
import {
    useSpeculativeProjectsInCluster,
    useTrackedQueueProjectsInCluster
} from "in_queue/contexts/ProjectDataContext";
import {
    getProjectDisplayName,
    TrackedProject
} from "in_queue/types/projectType";

interface AllProjectsItem {
    type: "all-projects";
}
interface TrackedProjectItem {
    type: "project";
    project: TrackedProject;
    displayName?: string;
}
type VisibilityMenuItem = AllProjectsItem | TrackedProjectItem;

export const VisibilityInput: React.FC<{
    parentProject: TrackedProject | undefined;
    setParentProject: (project: TrackedProject | undefined) => void;
    includedSpeculativeProjectIds: ReadonlyArray<string>;
}> = ({ parentProject, setParentProject, includedSpeculativeProjectIds }) => {
    const trackedProjects = useTrackedQueueProjectsInCluster();
    const speculativeProjects = useSpeculativeProjectsInCluster();

    let projectItems: VisibilityMenuItem[] = [{ type: "all-projects" }];
    if (trackedProjects !== "loading" && speculativeProjects !== "loading") {
        // List tracked projects first, then speculative projects, but ensure within each category they are sorted alphabetically by projectId / projectName
        const sortedtrackedProjects = [...trackedProjects].sort((a, b) =>
            a.projectId.localeCompare(b.projectId)
        );
        const includedSpeculativeProjects = speculativeProjects.filter((p) =>
            includedSpeculativeProjectIds.includes(p.projectId)
        );
        const sortedSpeculativeProjects = [...includedSpeculativeProjects].sort(
            (a, b) => a.projectName.localeCompare(b.projectName)
        );
        projectItems = projectItems.concat(
            [...sortedtrackedProjects, ...sortedSpeculativeProjects].map(
                (project) => ({
                    type: "project",
                    project,
                    displayName: getProjectDisplayName(project)
                })
            )
        );
    }

    return (
        <Select
            items={projectItems}
            itemRenderer={(item, { handleClick }) => (
                <MenuItem
                    active={
                        (item.type === "all-projects" && !parentProject) ||
                        (item.type === "project" &&
                            item.project.projectId === parentProject?.projectId)
                    }
                    text={
                        item.type === "all-projects"
                            ? "All projects"
                            : item.displayName
                    }
                    key={
                        item.type === "all-projects"
                            ? "All projects"
                            : item.project.projectId
                    }
                    onClick={handleClick}
                />
            )}
            filterable={false}
            popoverProps={{
                minimal: true
            }}
            onItemSelect={(item) => {
                setParentProject(
                    item.type === "project" ? item.project : undefined
                );
            }}
        >
            <Button
                rightIcon={IconNames.CARET_DOWN}
                text={
                    parentProject
                        ? getProjectDisplayName(parentProject)
                        : "All projects"
                }
                small
            />
        </Select>
    );
};
